import React from "react";
import { useTranslation } from "react-i18next";

import Spin from "components/spin/spin";
import Page from "components/page/Page";
import Callout from "components/callout/Callout";

import useTeeOnPayments from "./useTeeOnPayments";
import ActivateStepsPage from "./components/ActivateStepsPage";
import "./teeOnPayments.scss";
import TeeOnPaymentsSetup from "./components/TeeOnPaymentsSetup";
import AccountDashboard from "./components/AccountDashboard";
import { isTeeOnPaymentsSupported } from "./teeOnPaymentsTypes";

const teeOnPaymentsBreadcrumbs = [
  {
    label: "Settings",
    url: "/admin/settings",
  },
  {
    label: "Payments",
    url: "/admin/settings/payments/general",
  },
  {
    label: "Tee On Payments",
    url: "/admin/settings/payments/tee-on-payments",
  },
];

export default function TeeOnPayments() {
  const { t } = useTranslation();
  const {
    isLoading,
    isSaving,
    view,
    step,
    completedSteps,
    businessProfile,
    facilityCountryCode,
    payoutAccounts,
    bankAccounts,
    addedRepresentatives,
    representatives,
    processingFees,
    activationCountries,
    setBusinessProfile,
    startActivation,
    cancelActivation,
    saveBusinessAndContinue,
    addPayoutAccount,
    removePayoutAccount,
    continueFromPayoutConfiguration,
    addRepresentative,
    removeRepresentative,
    saveRepresentativeChanges,
    completeActivation,
    setStep,
  } = useTeeOnPayments();

  if (isLoading) {
    return (
      <div className="tee-on-payments-loading">
        <Spin />
      </div>
    );
  }

  if (!isTeeOnPaymentsSupported(facilityCountryCode)) {
    return (
      <Page
        title={t("Tee On Payments")}
        subtitle={t("View and manage available payment integrations for your facility.")}
        breadcrumbs={teeOnPaymentsBreadcrumbs}
      >
        <Callout
          type="warning"
          title={t("Tee On Payments is currently not available in your country")}
          content={t(
            "Stripe does not fully support Dominican Republic transactions at this time. Card processing and payouts through Tee On Payments cannot be activated for this facility. Please contact Tee On support if you have questions.",
          )}
        />
      </Page>
    );
  }

  if (view === "intro") {
    return <TeeOnPaymentsSetup onGetStarted={startActivation} />;
  }

  if (view === "activate") {
    return (
      <ActivateStepsPage
        step={step}
        completedSteps={completedSteps}
        businessProfile={businessProfile}
        facilityCountryCode={facilityCountryCode}
        countries={activationCountries}
        payoutAccounts={payoutAccounts}
        representatives={representatives}
        isSaving={isSaving}
        onBusinessProfileChange={setBusinessProfile}
        onBusinessContinue={saveBusinessAndContinue}
        onConfirmPayoutAccount={addPayoutAccount}
        onRemovePayoutAccount={removePayoutAccount}
        onPayoutContinue={continueFromPayoutConfiguration}
        onAddRepresentative={addRepresentative}
        onRemoveRepresentative={removeRepresentative}
        onActivationComplete={completeActivation}
        onStepChange={setStep}
        onCancel={cancelActivation}
      />
    );
  }

  return (
    <AccountDashboard
      businessProfile={businessProfile}
      bankAccounts={bankAccounts}
      representatives={addedRepresentatives}
      processingFees={processingFees}
      onSaveRepresentativeChanges={saveRepresentativeChanges}
    />
  );
}
